import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";

export const Monogram = ({ name, className = "" }) => {
    const initials = (name || "")
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("");
    return (
        <div className={`bg-[#0A0A0A] flex items-center justify-center ${className}`}>
            <span className="font-serif text-[#D4AF37] text-5xl md:text-6xl tracking-wide select-none">{initials}</span>
        </div>
    );
};

export default function Team() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/team")
            .then((r) => setMembers(r.data || []))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="team-page">
            <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-14 md:pb-20">
                <div className="eyebrow text-[#B8860B] mb-6">The People Behind the Podcast</div>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-8">
                    Meet the Team
                </h1>
                <p className="text-lg md:text-xl text-black/60 font-light max-w-2xl leading-relaxed">
                    The Gift of Kindness Podcast is carried by a small team with a big heart. Get to know
                    the people who bring every episode, program, and act of kindness to life.
                </p>
            </section>

            <section className="border-t border-black/10">
                {loading ? (
                    <div className="px-6 md:px-12 lg:px-20 py-20 font-serif text-2xl text-black/50">Loading…</div>
                ) : (
                    members.map((m, i) => (
                        <div
                            key={m.id}
                            data-testid={`team-member-row-${m.slug}`}
                            className="border-b border-black/10 px-6 md:px-12 lg:px-20 py-12 md:py-16 grid md:grid-cols-12 gap-8 md:gap-10 items-center hover:bg-[#FAFAFA] transition-colors duration-300"
                        >
                            <div className="md:col-span-1 font-serif text-5xl text-[#D4AF37]/60 tabular-nums">
                                {String(i + 1).padStart(2, "0")}
                            </div>
                            <div className="md:col-span-2">
                                {m.photo_url ? (
                                    <img
                                        src={m.photo_url}
                                        alt={m.name}
                                        className="w-40 h-40 md:w-44 md:h-44 object-cover object-top"
                                    />
                                ) : (
                                    <Monogram name={m.name} className="w-40 h-40 md:w-44 md:h-44" />
                                )}
                            </div>
                            <div className="md:col-span-6">
                                <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-2">{m.name}</h2>
                                <div className="eyebrow text-[#B8860B] text-[11px] mb-4">{m.role}</div>
                                <p className="text-black/60 font-light leading-relaxed line-clamp-3 max-w-xl">
                                    {(m.bio || "").split("\n")[0]}
                                </p>
                            </div>
                            <div className="md:col-span-3 md:text-right">
                                <Link
                                    to={`/team/${m.slug}`}
                                    data-testid={`view-profile-${m.slug}`}
                                    className="inline-flex items-center gap-3 border border-black px-7 py-3.5 eyebrow text-[11px] hover:bg-black hover:text-[#D4AF37] transition-colors duration-300"
                                >
                                    View Profile <ArrowRight size={14} />
                                </Link>
                            </div>
                        </div>
                    ))
                )}
            </section>

            <section className="bg-[#0A0A0A] text-white px-6 md:px-12 lg:px-20 py-20 md:py-28">
                <div className="grid md:grid-cols-12 items-center gap-10 max-w-6xl">
                    <div className="md:col-span-8">
                        <div className="eyebrow text-[#D4AF37] mb-6">Join The Movement</div>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                            Want to share your story on the show?
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/apply"
                            data-testid="team-cta"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 hover:bg-white transition-colors eyebrow"
                        >
                            Be a Guest <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
